// components/Survey/SurveyThemes.jsx
import React, { useState, useEffect } from 'react';
import { themesService } from '../../services/themesService';
import { 
  SparklesIcon, 
  CheckBadgeIcon, 
  ClockIcon, 
  ChartBarIcon,
  EyeIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  PlusIcon // Add this import
} from '@heroicons/react/24/solid';
import { motion, AnimatePresence } from 'framer-motion';

// Import the CreateTheme component
import CreateTheme from '../../components/Survey/CreateTheme'; 

const SurveyThemes = () => {
  const [themes, setThemes] = useState([]);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false); // Add this state
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [filter, setFilter] = useState('all');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [themesPerPage, setThemesPerPage] = useState(8); // Default for desktop

  useEffect(() => {
    loadThemes();
    
    // Adjust items per page based on screen size
    const handleResize = () => {
      if (window.innerWidth < 640) { // Mobile
        setThemesPerPage(4);
      } else if (window.innerWidth < 1024) { // Tablet
        setThemesPerPage(6);
      } else { // Desktop
        setThemesPerPage(8);
      }
    };

    handleResize(); // Set initial value
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadThemes = async () => {
    setLoading(true);
    try {
      const data = await themesService.getThemes();
      setThemes(data);
    } catch (error) {
      showMessage('Failed to load themes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (theme) => {
    try {
      const details = await themesService.getTheme(theme.id);
      setSelectedTheme(details);
      setShowDetailModal(true);
    } catch (error) {
      showMessage('Failed to load theme details', 'error');
    }
  };

  const handleApplyTheme = async (theme) => {
    setApplying(true);
    try {
      const result = await themesService.applyTheme({
        themeId: theme.id,
        themeName: theme.name,
        companyId: 1,
        surveyName: `${theme.name} Survey`
      });

      if (result.success) {
        showMessage(result.message, 'success');
        setShowDetailModal(false);
      }
    } catch (error) {
      showMessage(error.message || 'Failed to apply theme', 'error');
    } finally {
      setApplying(false);
    }
  };

  // Add this function to handle theme creation
  const handleThemeCreated = (newTheme) => {
    setShowCreateModal(false);
    loadThemes(); // Refresh the themes list
    showMessage(`Theme "${newTheme.name}" created successfully!`, 'success');
  };

  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 5000);
  };

  // Filter themes based on selected category
  const filteredThemes = filter === 'all' 
    ? themes 
    : themes.filter(theme => 
        filter === 'premium' ? theme.isPremium :
        theme.category.toLowerCase().includes(filter.toLowerCase())
      );

  // Pagination logic
  const indexOfLastTheme = currentPage * themesPerPage;
  const indexOfFirstTheme = indexOfLastTheme - themesPerPage;
  const currentThemes = filteredThemes.slice(indexOfFirstTheme, indexOfLastTheme);
  const totalPages = Math.ceil(filteredThemes.length / themesPerPage);

  // Generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pageNumbers.push(i);
        }
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          pageNumbers.push(i);
        }
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber !== '...' && pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      // Scroll to top of themes grid
      window.scrollTo({ top: 400, behavior: 'smooth' });
    }
  };

  const categories = ['all', 'premium', ...new Set(themes.map(theme => theme.category))];

  const getColorClasses = (colorScheme) => {
    const colorMap = {
      blue: 'from-blue-500 to-cyan-500',
      green: 'from-green-500 to-emerald-500',
      purple: 'from-purple-500 to-violet-500',
      orange: 'from-orange-500 to-amber-500',
      red: 'from-red-500 to-rose-500',
      teal: 'from-teal-500 to-cyan-500',
      indigo: 'from-indigo-500 to-blue-500',
      yellow: 'from-yellow-500 to-amber-500',
      cyan: 'from-cyan-500 to-blue-500',
      pink: 'from-pink-500 to-rose-500',
      emerald: 'from-emerald-500 to-green-500',
      violet: 'from-violet-500 to-purple-500',
      gold: 'from-yellow-500 to-amber-500',
      navy: 'from-blue-800 to-indigo-800'
    };
    return colorMap[colorScheme] || 'from-gray-500 to-gray-600';
  };

  const getBgColorClasses = (colorScheme) => {
    const colorMap = {
      blue: 'bg-blue-50 border-blue-200',
      green: 'bg-green-50 border-green-200',
      purple: 'bg-purple-50 border-purple-200',
      orange: 'bg-orange-50 border-orange-200',
      red: 'bg-red-50 border-red-200',
      teal: 'bg-teal-50 border-teal-200',
      indigo: 'bg-indigo-50 border-indigo-200',
      yellow: 'bg-yellow-50 border-yellow-200',
      cyan: 'bg-cyan-50 border-cyan-200',
      pink: 'bg-pink-50 border-pink-200',
      emerald: 'bg-emerald-50 border-emerald-200',
      violet: 'bg-violet-50 border-violet-200',
      gold: 'bg-amber-50 border-amber-200',
      navy: 'bg-blue-50 border-blue-300'
    };
    return colorMap[colorScheme] || 'bg-gray-50 border-gray-200';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 p-4 sm:p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <motion.h1 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
          >
            Great Work Place Club - Survey Themes
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto px-4"
          >
            Choose from professionally designed survey templates to get started quickly
          </motion.p>
          
          {/* Add Create Theme Button Here */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mt-6"
          >
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md mx-auto"
            >
              <PlusIcon className="w-5 h-5" />
              Create New Theme
            </button>
          </motion.div>
        </div>

        {message.text && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className={`p-4 rounded-lg mb-6 max-w-4xl mx-auto ${
              message.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}
          >
            <div className="flex items-center justify-center">
              <CheckBadgeIcon className={`w-5 h-5 mr-2 ${
                message.type === 'success' ? 'text-green-500' : 'text-red-500'
              }`} />
              {message.text}
            </div>
          </motion.div>
        )}

        {/* Filter Tabs */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-2 mb-8 px-2"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => {
                setFilter(category);
                setCurrentPage(1); // Reset to first page when filter changes
              }}
              className={`px-3 py-2 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium capitalize transition-all ${
                filter === category
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {category === 'all' ? 'All Themes' : 
               category === 'premium' ? '⭐ Premium' : category}
            </button>
          ))}
        </motion.div>

        {/* Results Count and Page Info */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 px-2">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-sm text-gray-600 mb-2 sm:mb-0"
          >
            Showing {indexOfFirstTheme + 1}-{Math.min(indexOfLastTheme, filteredThemes.length)} of {filteredThemes.length} themes
          </motion.div>
          
          {/* Items Per Page Selector */}
          <motion.div 
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center space-x-2"
          >
            <span className="text-sm text-gray-600">Show:</span>
            <select
              value={themesPerPage}
              onChange={(e) => {
                setThemesPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-gray-300 rounded-lg px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={4}>4</option>
              <option value={6}>6</option>
              <option value={8}>8</option>
              <option value={12}>12</option>
              <option value={16}>16</option>
            </select>
            <span className="text-sm text-gray-600">per page</span>
          </motion.div>
        </div>

        {/* Themes Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <>
            <motion.div 
              layout
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6 max-w-7xl mx-auto"
            >
              {currentThemes.map((theme, index) => (
                <motion.div
                  key={theme.id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                  className={`bg-white rounded-2xl shadow-sm border-2 overflow-hidden group cursor-pointer ${getBgColorClasses(theme.colorScheme)}`}
                >
                  {/* Theme Header */}
                  <div className={`h-2 bg-gradient-to-r ${getColorClasses(theme.colorScheme)}`} />
                  
                  <div className="p-4 sm:p-6">
                    {/* Theme Icon and Premium Badge */}
                    <div className="flex justify-between items-start mb-3 sm:mb-4">
                      <div className="text-2xl sm:text-3xl">{theme.icon}</div>
                      {theme.isPremium && (
                        <span className="flex items-center gap-1 bg-gradient-to-r from-amber-500 to-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                          <SparklesIcon className="w-3 h-3" />
                          Premium
                        </span>
                      )}
                    </div>

                    {/* Theme Name and Description */}
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors line-clamp-1">
                      {theme.name}
                    </h3>
                    <p className="text-gray-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">
                      {theme.description}
                    </p>

                    {/* Theme Stats */}
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-3 sm:mb-4">
                      <div className="flex items-center gap-1">
                        <ChartBarIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        {theme.questionCount} questions
                      </div>
                      <div className="flex items-center gap-1">
                        <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        {theme.estimatedTime}
                      </div>
                    </div>

                    {/* Popularity Bar */}
                    <div className="mb-3 sm:mb-4">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Popularity</span>
                        <span>{theme.popularity}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full bg-gradient-to-r ${getColorClasses(theme.colorScheme)}`}
                          style={{ width: `${theme.popularity}%` }}
                        />
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewDetails(theme)}
                        className="flex-1 bg-white text-gray-700 border border-gray-300 rounded-lg py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                      >
                        <EyeIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                        <span className="hidden sm:inline">View Details</span>
                        <span className="sm:hidden">View</span>
                      </button>
                      <button
                        onClick={() => handleApplyTheme(theme)}
                        className="flex-1 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg py-2 px-2 sm:px-3 text-xs sm:text-sm font-medium hover:from-blue-700 hover:to-cyan-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-1"
                      >
                        <span>Apply</span>
                        <ArrowRightIcon className="w-3 h-3 sm:w-4 sm:h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* Pagination */}
            {totalPages > 1 && (
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="flex flex-col sm:flex-row items-center justify-between mt-8 space-y-4 sm:space-y-0 px-2"
              >
                {/* Page Info */}
                <div className="text-sm text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-1 sm:space-x-2">
                  {/* First Page */}
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="First Page"
                  >
                    <ChevronDoubleLeftIcon className="w-4 h-4" />
                  </button>

                  {/* Previous Page */}
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Previous Page"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>

                  {/* Page Numbers */}
                  <div className="flex space-x-1">
                    {getPageNumbers().map((pageNumber, index) => (
                      <button
                        key={index}
                        onClick={() => handlePageChange(pageNumber)}
                        className={`min-w-[2.5rem] px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                          pageNumber === currentPage
                            ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                            : pageNumber === '...'
                            ? 'border-transparent text-gray-500 cursor-default'
                            : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                        }`}
                        disabled={pageNumber === '...'}
                      >
                        {pageNumber}
                      </button>
                    ))}
                  </div>

                  {/* Next Page */}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Next Page"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>

                  {/* Last Page */}
                  <button
                    onClick={() => handlePageChange(totalPages)}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                    title="Last Page"
                  >
                    <ChevronDoubleRightIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Quick Jump */}
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Go to:</span>
                  <input
                    type="number"
                    min="1"
                    max={totalPages}
                    value={currentPage}
                    onChange={(e) => {
                      const page = parseInt(e.target.value);
                      if (page >= 1 && page <= totalPages) {
                        handlePageChange(page);
                      }
                    }}
                    className="w-16 px-2 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </motion.div>
            )}
          </>
        )}

        {filteredThemes.length === 0 && !loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <div className="w-24 h-24 bg-gradient-to-br from-gray-200 to-gray-300 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <SparklesIcon className="w-12 h-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No themes found</h3>
            <p className="text-gray-600">Try selecting a different filter category</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="mt-4 flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md mx-auto"
            >
              <PlusIcon className="w-5 h-5" />
              Create Your First Theme
            </button>
          </motion.div>
        )}
      </div>

      {/* Theme Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedTheme && (
          <ThemeDetailModal
            theme={selectedTheme}
            onClose={() => setShowDetailModal(false)}
            onApply={handleApplyTheme}
            applying={applying}
            getColorClasses={getColorClasses}
            getBgColorClasses={getBgColorClasses}
          />
        )}
      </AnimatePresence>

      {/* Create Theme Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateTheme
            onThemeCreated={handleThemeCreated}
            onCancel={() => setShowCreateModal(false)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Theme Detail Modal Component (unchanged)
const ThemeDetailModal = ({ theme, onClose, onApply, applying, getColorClasses, getBgColorClasses }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`bg-gradient-to-r ${getColorClasses(theme.colorScheme)} p-6 text-white`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">{theme.icon}</div>
              <div>
                <h2 className="text-2xl font-bold">{theme.name}</h2>
                <p className="text-blue-100">{theme.description}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 transition-colors p-2 hover:bg-blue-600 rounded-lg"
            >
              <span className="text-2xl">×</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Theme Info */}
              <div className={`p-4 rounded-xl ${getBgColorClasses(theme.colorScheme)}`}>
                <h3 className="font-semibold text-gray-800 mb-3">Theme Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Category:</span>
                    <span className="font-medium">{theme.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Questions:</span>
                    <span className="font-medium">{theme.questionCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Estimated Time:</span>
                    <span className="font-medium">{theme.estimatedTime}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Popularity:</span>
                    <span className="font-medium">{theme.popularity}%</span>
                  </div>
                </div>
              </div>

              {/* Best For */}
              <div className="p-4 rounded-xl bg-gray-50">
                <h3 className="font-semibold text-gray-800 mb-3">Best For</h3>
                <div className="flex flex-wrap gap-2">
                  {theme.bestFor.map((useCase, index) => (
                    <span key={index} className="bg-white text-gray-700 px-3 py-1 rounded-full text-sm border">
                      {useCase}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Sample Questions */}
              <div className="p-4 rounded-xl bg-gray-50">
                <h3 className="font-semibold text-gray-800 mb-3">Sample Questions</h3>
                <ul className="space-y-2 text-sm">
                  {theme.sampleQuestions.map((question, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-blue-500 mt-1">•</span>
                      {question}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Tags */}
              <div className="p-4 rounded-xl bg-gray-50">
                <h3 className="font-semibold text-gray-800 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {theme.tags.map((tag, index) => (
                    <span key={index} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Ready to create your survey with this theme?
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={() => onApply(theme)}
                disabled={applying}
                className="px-8 py-2 bg-gradient-to-r from-blue-600 to-cyan-600 text-white rounded-lg hover:from-blue-700 hover:to-cyan-700 disabled:opacity-50 transition-colors font-medium flex items-center gap-2 shadow-sm"
              >
                {applying ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Applying...
                  </>
                ) : (
                  <>
                    <CheckBadgeIcon className="w-4 h-4" />
                    Apply Theme
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default SurveyThemes;